export const LOTTERY_JAR_VISIBLE_LIMIT = 48;
export const LOTTERY_CHAMBER_BOUNDS = Object.freeze({ cx: 0.5, cy: 0.5, rx: 0.43, ry: 0.43 });

export function lotteryVisibleLimitForWidth(width) {
  const safeWidth = Number(width);
  if (Number.isFinite(safeWidth) && safeWidth < 430) return 20;
  if (Number.isFinite(safeWidth) && safeWidth < 720) return 36;
  return LOTTERY_JAR_VISIBLE_LIMIT;
}

function normalizedText(value) {
  return String(value ?? "").trim();
}

function hashText(value) {
  let hash = 2166136261;
  for (const character of String(value)) {
    hash ^= character.codePointAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function seededUnit(value) {
  return (hashText(value) % 100000) / 100000;
}

export function selectLotteryStudent(students, { mode = "pure", excludedIds = [], counts = {}, random = Math.random } = {}) {
  const excluded = new Set(excludedIds);
  const pool = students.filter((student) => !excluded.has(student.id));
  if (!pool.length) throw new Error("没有可参与点名的学生");
  const randomValue = Number(random());
  if (!Number.isFinite(randomValue) || randomValue < 0 || randomValue >= 1) throw new Error("随机源必须返回 0（含）到 1（不含）之间的有限数值");
  if (mode === "pure") return pool[Math.floor(randomValue * pool.length)];
  if (mode !== "weighted") throw new Error("点名模式无效");
  const weights = pool.map((student) => 1 / ((Number.isFinite(Number(counts[student.id])) && Number(counts[student.id]) >= 0 ? Number(counts[student.id]) : 0) + 1));
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  let cursor = randomValue * total;
  for (let index = 0; index < pool.length; index += 1) {
    cursor -= weights[index];
    if (cursor <= 0) return pool[index];
  }
  return pool.at(-1);
}

function sampledStudents(students, visibleLimit) {
  if (students.length <= visibleLimit) return students;
  const individualLimit = Math.max(1, visibleLimit - 1);
  return Array.from({ length: individualLimit }, (_, index) => students[Math.min(students.length - 1, Math.floor(index * students.length / individualLimit))]);
}

function staticPosition(index, count, seed) {
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  const radial = Math.sqrt((index + 0.72) / (count + 0.35)) * 0.82;
  const angle = index * goldenAngle + seededUnit(`${seed}:layout`) * 0.3;
  return {
    x: 0.5 + Math.cos(angle) * radial * 0.42,
    y: 0.51 + Math.sin(angle) * radial * 0.40,
  };
}

export function buildLotteryJarModel(students, { contextKey = "", visibleLimit = LOTTERY_JAR_VISIBLE_LIMIT } = {}) {
  const safeStudents = Array.isArray(students) ? students.filter((student) => student && normalizedText(student.name)) : [];
  const shown = sampledStudents(safeStudents, visibleLimit);
  const aggregateCount = safeStudents.length - shown.length;
  const itemCount = shown.length + (aggregateCount > 0 ? 1 : 0);
  const balls = shown.map((student, index) => {
    const seed = `${contextKey}|${student.id}|${student.name}|${index}`;
    const position = staticPosition(index, itemCount, seed);
    return {
      id: String(student.id),
      name: normalizedText(student.name),
      studentNo: normalizedText(student.studentNo),
      x: position.x,
      y: position.y,
      rotate: Math.round((seededUnit(`${seed}:rotate`) - 0.5) * 16),
      tone: index % 4,
      aggregate: false,
    };
  });
  if (aggregateCount > 0) {
    const position = staticPosition(balls.length, itemCount, `${contextKey}:aggregate`);
    balls.push({
      id: "aggregate",
      name: `其余 ${aggregateCount} 人`,
      studentNo: "",
      x: position.x,
      y: position.y,
      rotate: 0,
      tone: 3,
      aggregate: true,
    });
  }
  return { eligibleCount: safeStudents.length, shownCount: shown.length, aggregateCount, balls };
}

function simulationRadius(count) {
  if (count <= 20) return 0.058;
  if (count <= 36) return 0.047;
  return 0.038;
}

export function createLotteryPhysics(model, { contextKey = "" } = {}) {
  const radius = simulationRadius(model.balls.length);
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  return {
    time: 0,
    bounds: { ...LOTTERY_CHAMBER_BOUNDS },
    wallCollisions: 0,
    balls: model.balls.map((ball, index) => {
      const seed = `${contextKey}|${ball.id}|${index}`;
      const angle = index * goldenAngle + seededUnit(`${seed}:velocity-angle`) * 0.42;
      const speed = 1.02 + seededUnit(`${seed}:velocity-speed`) * 0.46;
      return {
        ...ball,
        radius,
        startX: ball.x,
        startY: ball.y,
        minX: ball.x,
        maxX: ball.x,
        minY: ball.y,
        maxY: ball.y,
        distance: 0,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 0.34,
        depth: 0.82 + seededUnit(`${seed}:depth`) * 0.28,
        blurFactor: index % 4 === 0 ? 0 : 0.45 + seededUnit(`${seed}:blur`) * 0.55,
        vortexSign: index % 5 === 0 ? -1 : 1,
        jetOffset: (seededUnit(`${seed}:jet-offset`) - 0.5) * 0.34,
        noisePhase: seededUnit(`${seed}:noise`) * Math.PI * 2,
      };
    }),
  };
}

function containInEllipse(ball, bounds, state) {
  const usableRx = bounds.rx - ball.radius;
  const usableRy = bounds.ry - ball.radius;
  const qx = (ball.x - bounds.cx) / usableRx;
  const qy = (ball.y - bounds.cy) / usableRy;
  const length = Math.hypot(qx, qy);
  if (length <= 1) return;
  state.wallCollisions += 1;
  const nx = qx / length;
  const ny = qy / length;
  ball.x = bounds.cx + nx * usableRx * 0.998;
  ball.y = bounds.cy + ny * usableRy * 0.998;
  const worldNx = nx / usableRx;
  const worldNy = ny / usableRy;
  const normalLength = Math.hypot(worldNx, worldNy) || 1;
  const ux = worldNx / normalLength;
  const uy = worldNy / normalLength;
  const outward = ball.vx * ux + ball.vy * uy;
  if (outward > 0) {
    ball.vx -= outward * ux * 1.82;
    ball.vy -= outward * uy * 1.82;
  }
}

function resolveBallCollisions(balls) {
  for (let left = 0; left < balls.length; left += 1) {
    for (let right = left + 1; right < balls.length; right += 1) {
      const a = balls[left];
      const b = balls[right];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const distance = Math.hypot(dx, dy) || 0.0001;
      const minimum = a.radius + b.radius;
      if (distance >= minimum) continue;
      const nx = dx / distance;
      const ny = dy / distance;
      const correction = (minimum - distance) * 0.51;
      a.x -= nx * correction;
      a.y -= ny * correction;
      b.x += nx * correction;
      b.y += ny * correction;
      const relative = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny;
      if (relative >= 0) continue;
      const impulse = -relative * 0.78;
      a.vx -= nx * impulse;
      a.vy -= ny * impulse;
      b.vx += nx * impulse;
      b.vy += ny * impulse;
    }
  }
}

function physicsSubstep(state, dt) {
  state.time += dt;
  const { bounds, balls } = state;
  for (const ball of balls) {
    const dx = ball.x - bounds.cx;
    const dy = ball.y - bounds.cy;
    const distance = Math.max(0.08, Math.hypot(dx, dy));
    const bottomLift = Math.max(0, (ball.y - 0.34) / 0.58);
    const fanColumn = Math.exp(-Math.pow((dx - ball.jetOffset) / 0.19, 2));
    const tangentialX = -dy / distance;
    const tangentialY = dx / distance;
    const pulse = Math.sin(state.time * 11.2 + ball.noisePhase);
    const crossPulse = Math.cos(state.time * 7.7 + ball.noisePhase * 1.7);
    const vortexForce = (2.15 + bottomLift * 1.1) * ball.vortexSign;
    const crossJet = Math.sin(state.time * 5.2 + ball.noisePhase * 2.3) * (1.2 + fanColumn * 1.8);
    const ax = tangentialX * vortexForce + pulse * 2.75 + crossPulse * 1.05 + crossJet;
    const ay = tangentialY * vortexForce - fanColumn * (2.8 + bottomLift * 5.4) + crossPulse * 1.65 + pulse * .7 + 0.38;
    ball.vx += ax * dt;
    ball.vy += ay * dt;
    const damping = Math.exp(-0.48 * dt);
    ball.vx *= damping;
    ball.vy *= damping;
    const speed = Math.hypot(ball.vx, ball.vy);
    const maximumSpeed = 1.62;
    if (speed > maximumSpeed) {
      ball.vx = ball.vx / speed * maximumSpeed;
      ball.vy = ball.vy / speed * maximumSpeed;
    }
    const previousX = ball.x;
    const previousY = ball.y;
    ball.x += ball.vx * dt;
    ball.y += ball.vy * dt;
    ball.depth = 0.78 + (Math.sin(state.time * 5.4 + ball.noisePhase + ball.x * 4) + 1) * 0.16;
    containInEllipse(ball, bounds, state);
    ball.distance += Math.hypot(ball.x - previousX, ball.y - previousY);
    ball.minX = Math.min(ball.minX, ball.x);
    ball.maxX = Math.max(ball.maxX, ball.x);
    ball.minY = Math.min(ball.minY, ball.y);
    ball.maxY = Math.max(ball.maxY, ball.y);
  }
  resolveBallCollisions(balls);
  for (const ball of balls) containInEllipse(ball, bounds, state);
}

export function stepLotteryPhysics(state, deltaSeconds) {
  if (!state || !Array.isArray(state.balls)) throw new Error("点名球仓物理状态无效");
  const safeDelta = Number(deltaSeconds);
  if (!Number.isFinite(safeDelta) || safeDelta < 0) throw new Error("动画时间步长必须是非负有限数值");
  let remaining = Math.min(safeDelta, 0.12);
  const maximumSubstep = 1 / 120;
  while (remaining > 0.0000001) {
    const step = Math.min(maximumSubstep, remaining);
    physicsSubstep(state, step);
    remaining -= step;
  }
  return state;
}

export function lotteryPhysicsMetrics(state) {
  const total = state.balls.length || 1;
  const active = state.balls.filter((ball) => Math.hypot(ball.vx, ball.vy) >= 0.28).length;
  const crossChamber = state.balls.filter((ball) => Math.max(ball.maxX - ball.minX, ball.maxY - ball.minY) >= 0.5).length;
  const maximumContainment = state.balls.reduce((maximum, ball) => {
    const usableRx = state.bounds.rx - ball.radius;
    const usableRy = state.bounds.ry - ball.radius;
    return Math.max(maximum, Math.hypot((ball.x - state.bounds.cx) / usableRx, (ball.y - state.bounds.cy) / usableRy));
  }, 0);
  return {
    activeRatio: active / total,
    crossChamberRatio: crossChamber / total,
    wallCollisions: state.wallCollisions,
    maximumContainment,
    averageDistance: state.balls.reduce((sum, ball) => sum + ball.distance, 0) / total,
  };
}

function element(tagName, className, text) {
  const node = document.createElement(tagName);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function updateBallNode(node, ball) {
  node.style.setProperty("--ball-x", (ball.x * 100).toFixed(3));
  node.style.setProperty("--ball-y", (ball.y * 100).toFixed(3));
  node.style.setProperty("--ball-depth", ball.depth.toFixed(3));
  node.style.setProperty("--ball-spin", `${Math.round((ball.x + ball.y + ball.distance) * 180)}deg`);
  node.style.setProperty("--ball-blur", `${Math.min(1.35, Math.hypot(ball.vx, ball.vy) * ball.blurFactor).toFixed(3)}px`);
}

export function startLotteryPhysicsAnimation(container, model, { contextKey = "" } = {}) {
  if (!container || typeof requestAnimationFrame !== "function") return () => {};
  const state = createLotteryPhysics(model, { contextKey });
  const nodes = new Map([...container.querySelectorAll(".lottery-ball")].map((node) => [node.dataset.studentId, node]));
  let frameId = 0;
  let previous = performance.now();
  let active = true;
  const frame = (now) => {
    if (!active) return;
    stepLotteryPhysics(state, Math.max(0, (now - previous) / 1000));
    previous = now;
    for (const ball of state.balls) {
      const node = nodes.get(ball.id);
      if (node) updateBallNode(node, ball);
    }
    const metrics = lotteryPhysicsMetrics(state);
    container.dataset.physicsFrames = String(Number(container.dataset.physicsFrames ?? 0) + 1);
    container.dataset.activeBallRatio = metrics.activeRatio.toFixed(3);
    container.dataset.crossChamberRatio = metrics.crossChamberRatio.toFixed(3);
    container.dataset.wallCollisions = String(metrics.wallCollisions);
    container.dataset.maximumContainment = metrics.maximumContainment.toFixed(4);
    container.__lotteryPhysicsState = state;
    frameId = requestAnimationFrame(frame);
  };
  frameId = requestAnimationFrame(frame);
  return () => {
    active = false;
    cancelAnimationFrame(frameId);
  };
}

export function renderLotteryJar(container, { students = [], contextKey = "", phase = "idle", winner = null, status = "准备点名", reducedMotion = false } = {}) {
  if (!(container instanceof Element)) throw new Error("随机点名球仓需要有效容器");
  container.__lotteryMotionStop?.();
  const model = buildLotteryJarModel(students, { contextKey, visibleLimit: lotteryVisibleLimitForWidth(container.clientWidth || window.innerWidth) });
  container.className = `lottery-jar lottery-jar--${phase}${model.balls.length > 32 ? " lottery-jar--dense" : ""}`;
  container.dataset.phase = phase;
  container.dataset.eligibleCount = String(model.eligibleCount);
  container.dataset.physicsFrames = "0";
  container.dataset.wallCollisions = "0";
  container.dataset.maximumContainment = "0";
  container.setAttribute("role", "group");
  container.setAttribute("aria-label", `随机点名球仓，可参与 ${model.eligibleCount} 人`);

  const heading = element("div", "lottery-jar__heading lottery-sr-only");
  const count = element("span", "lottery-jar__count", `可参与 ${model.eligibleCount} 人`);
  const statusNode = element("span", "lottery-jar__status", status);
  statusNode.setAttribute("role", "status");
  statusNode.setAttribute("aria-live", "polite");
  heading.append(count, statusNode);

  const apparatus = element("div", "lottery-machine__apparatus");
  const chamber = element("div", "lottery-machine__chamber");
  chamber.append(element("div", "lottery-machine__glass-rim"), element("div", "lottery-machine__light-beam"));
  const pool = element("div", "lottery-jar__pool");
  for (const ball of model.balls) {
    const ballNode = element("span", `lottery-ball lottery-ball--tone-${ball.tone}${ball.aggregate ? " lottery-ball--aggregate" : ""}`, ball.name);
    ballNode.dataset.studentId = ball.id;
    ballNode.title = ball.studentNo ? `${ball.name} · ${ball.studentNo}` : ball.name;
    ballNode.style.setProperty("--ball-x", (ball.x * 100).toFixed(3));
    ballNode.style.setProperty("--ball-y", (ball.y * 100).toFixed(3));
    ballNode.style.setProperty("--ball-depth", "1");
    ballNode.style.setProperty("--ball-spin", `${ball.rotate}deg`);
    ballNode.style.setProperty("--ball-blur", "0px");
    if (winner && ball.id === String(winner.id)) ballNode.classList.add("lottery-ball--winner-source");
    pool.append(ballNode);
  }
  if (!model.eligibleCount) pool.append(element("p", "lottery-jar__empty", status));
  chamber.append(pool);

  const duct = element("div", "lottery-machine__duct");
  duct.append(element("div", "lottery-machine__duct-neck"), element("div", "lottery-machine__duct-drop"));
  const routedBall = element("div", "lottery-machine__routed-ball", winner ? normalizedText(winner.name) : "");
  routedBall.dataset.studentId = winner ? String(winner.id) : "";
  duct.append(routedBall);

  const base = element("div", "lottery-machine__base");
  base.append(element("div", "lottery-machine__fan"), element("div", "lottery-machine__fan-light"));
  const tray = element("div", "lottery-machine__tray");
  if (winner && phase === "revealed") {
    const trayBall = element("div", "lottery-machine__tray-ball", normalizedText(winner.name));
    trayBall.dataset.studentId = String(winner.id);
    tray.append(trayBall);
  }
  apparatus.append(chamber, duct, base, tray);

  const reveal = element("div", "lottery-jar__reveal");
  reveal.setAttribute("aria-live", "polite");
  if (winner && phase === "revealed") {
    const winnerText = element("div", "lottery-jar__winner-text");
    winnerText.append(element("strong", "", normalizedText(winner.name)));
    reveal.append(winnerText);
  }
  container.replaceChildren(heading, apparatus, reveal);
  const shouldReduceMotion = reducedMotion || (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false);
  if (phase === "mixing" && !shouldReduceMotion) container.__lotteryMotionStop = startLotteryPhysicsAnimation(container, model, { contextKey });
  else container.__lotteryMotionStop = () => {};
  return model;
}

export function lotteryMotionTimings(reducedMotion = false) {
  return reducedMotion ? { mixMs: 90, routeMs: 150 } : { mixMs: 2300, routeMs: 900 };
}

export async function runLotterySequence({ reducedMotion = false, onPhase, wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds)), isCancelled = () => false } = {}) {
  const timings = lotteryMotionTimings(reducedMotion);
  onPhase?.("mixing");
  await wait(timings.mixMs);
  if (isCancelled()) return false;
  onPhase?.("routing");
  await wait(timings.routeMs);
  if (isCancelled()) return false;
  onPhase?.("revealed");
  return true;
}
